import React, { useEffect, useState } from "react";
import api from "../../../axiosConfig";
import { Button, Form, InputGroup, Spinner, Alert } from "react-bootstrap";
import * as XLSX from "xlsx";
import { useSearchParams, useNavigate } from "react-router-dom";
import { popup } from "../../common/popup";

export default function StockHistoryTab() {
  const [histories, setHistories] = useState([]);
  const [filteredHistories, setFilteredHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // 🔴 WAJIB ADA SEBELUM useEffect
  const stockItemIdFromUrl = searchParams.get("stock_item_id");
  const typeFromUrl = searchParams.get("type");

  useEffect(() => {
    if (typeFromUrl) setTypeFilter(typeFromUrl);
  }, [typeFromUrl]);

  useEffect(() => {
    fetchHistories();
  }, [stockItemIdFromUrl]);

  useEffect(() => {
    applyFilters();
  }, [histories, search, typeFilter, startDate, endDate]);

  const fetchHistories = async () => {
    try {
      setLoading(true);

      const params = {};
      if (stockItemIdFromUrl) {
        params.stock_item_id = stockItemIdFromUrl;
      }

      const res = await api.get("/stock-items/history", { params });

      // 🔴 FILTER WAJIB: HANYA ITEM (SHORT & LONG)
      const filtered = (res.data || []).filter(
        (h) => h.lifetime_type === "short" || h.lifetime_type === "long",
      );

      setHistories(filtered);
    } catch (err) {
      console.error(err);
      setError("❌ Gagal mengambil data history stock");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let data = [...histories];

    // 🔍 Filter text
    if (search) {
      const lower = search.toLowerCase();
      data = data.filter(
        (h) =>
          h.part_name?.toLowerCase().includes(lower) ||
          h.spesifikasi?.toLowerCase().includes(lower) ||
          h.note?.toLowerCase().includes(lower),
      );
    }

    // 🔁 Filter stock in / out
    if (typeFilter === "in") {
      data = data.filter((h) => h.qty_in > 0);
    } else if (typeFilter === "out") {
      data = data.filter((h) => h.qty_out > 0);
    }

    // 📅 FILTER TANGGAL
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      data = data.filter((h) => new Date(h.created_at) >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      data = data.filter((h) => new Date(h.created_at) <= end);
    }

    setFilteredHistories(data);
  };

  const exportToExcel = () => {
    if (filteredHistories.length === 0) {
      popup.alert("Data masih kosong");
      return;
    }

    const excelData = filteredHistories.map((h, i) => ({
      No: i + 1,
      Tanggal: new Date(h.created_at).toLocaleString("id-ID", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
      "Nama Barang": h.part_name,
      Spesifikasi: h.spesifikasi || "-",
      "Stock Awal": h.stock_before,
      Masuk: h.qty_in > 0 ? h.qty_in : "-",
      Keluar: h.qty_out > 0 ? h.qty_out : "-",
      "Stock Akhir": h.stock_after,
      Keterangan: h.note || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "History Stock");
    XLSX.writeFile(workbook, "History_Stock.xlsx");
  };

  return (
    <div
      className="shortlife-container"
      style={{ marginTop: "-25px", height: "calc(108vh - 120px)" }}
    >
      <div className="header-bar mb-3">
        <h2>HISTORY STOCK</h2>
        <Button variant="success" onClick={exportToExcel}>
          Export Excel
        </Button>
      </div>

      <div
        className="mb-3 d-flex justify-content-between align-items-end flex-wrap gap-3"
        style={{ marginTop: "-20px" }}
      >
        {/* 🔍 SEARCH & TYPE FILTER */}
        <div
          className="d-flex gap-2"
          style={{ maxWidth: 600, flexWrap: "wrap" }}
        >
          <InputGroup style={{ width: 360 }}>
            <Form.Control
              placeholder="🔍 Cari..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          <Form.Select
            style={{ width: 200 }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">Semua</option>
            <option value="in">Stock In</option>
            <option value="out">Stock Out</option>
          </Form.Select>
        </div>

        {/* 📅 FILTER TANGGAL (KANAN) */}
        <div className="d-flex gap-2 flex-wrap align-items-end">
          <Form.Group>
            <Form.Label className="mb-1">Dari</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="mb-1">Sampai</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Form.Group>

          <Button
            variant="danger"
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setSearch("");
              setTypeFilter("all");
              applyFilters(); // panggil ulang filter
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div
          className="text-center p-5"
          style={{ height: "calc(100vh - 120px)" }}
        >
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="preview-table">
            <thead>
              <tr>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Tanggal
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Nama Barang
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Spesifikasi
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Stock Awal
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Stock In
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Stock Out
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Stock Akhir
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Keterangan
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredHistories.length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty">
                    Data belum ada
                  </td>
                </tr>
              ) : (
                filteredHistories.map((h, i) => (
                  <tr key={h.id}>
                    <td className="center">{i + 1}</td>
                    <td>
                      {new Date(h.created_at).toLocaleString("id-ID", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="bold cell-left">{h.part_name}</td>
                    <td className="cell-left">{h.spesifikasi || "-"}</td>
                    <td className="center">{h.stock_before}</td>
                    <td className="text-success fw-bold center">
                      {h.qty_in > 0 ? h.qty_in : ""}
                    </td>
                    <td className="center">
                      {h.qty_out > 0 ? (
                        <Button
                          variant="link"
                          className="text-danger fw-bold p-0"
                          onClick={() =>
                            navigate(
                              `/maintenance-dashboard/stock/history-usage?stock_item_id=${h.stock_item_id}`,
                            )
                          }
                        >
                          {h.qty_out}
                        </Button>
                      ) : (
                        ""
                      )}
                    </td>

                    <td className="center">{h.stock_after}</td>
                    <td className="cell-left">{h.note || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
